import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Json } from 'src/app/_shared/interface/common.interface';
import { AppState } from 'src/app/app.state';
import { PropertyState } from 'src/app/workspace/state/property.state';
import { ClientDashboardService } from '../../service/client-dashboard.service';
import { ClientDashboardState } from '../../state/client-dashboard.state';

@Component({
  selector: 'app-past-clients-list',
  templateUrl: './past-clients-list.component.html',
  styleUrls: ['./past-clients-list.component.scss'],
})
export class PastClientsListComponent implements OnInit, AfterViewInit {
  constructor(
    public clientDashboardState: ClientDashboardState,
    public clientDashboardService: ClientDashboardService,
    public formBuilder: FormBuilder,
    public appState: AppState,
    private router: Router,
    private propertyState: PropertyState
  ) { }
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @Output() reset = new EventEmitter<string>();
  @Input() isLtv:boolean;
  @Input() isLongTerm:boolean;
  @Input() limitRows: number | null = null; // null means show all
  @Input() displayedColumns: string[] = [];
  @Input() isHideFilter: boolean = false;
  @ViewChild('sortedTable', { read: MatSort }) sort!: MatSort;
  selection = new Set<any>();
  pastClientsFetchParams = { orderBy: '', orderType: '', limit: 10, offset: 0 };
  tableDataSource = new MatTableDataSource<any>([]);
  pastUsersCount: number;
  pageSize: any;
  pageIndex: number;
  onDestroyNotifier$ = new Subject();
  pastClientInfo: any;
  openAddContactNoteSheetValue =false;
  openCreateMailSheetValue = false;
  tableColumnList: string[] = [];
  ownedForYears = Array.from({ length: 21 }, (_, i) => i); // 0 - 20
  minMaxValues = Array.from({ length: 101 }, (_, i) => i); // 0 - 100
  selectedRows: any;
  pastFilterForm: FormGroup = this.formBuilder.group(
    {
      searchClients: '',
      ltvMin: [''],
      ltvMax: [''],
      transactionType: [''],
      ownedMin: [''],
      ownedMax: [''],
    },
    {
      validators: [
        this.minMaxValidator('ltvMin', 'ltvMax', 'ltvRangeInvalid'),
        this.minMaxValidator('ownedMin', 'ownedMax', 'ownedRangeInvalid'),
      ],
    }
  );

  transactionType = ['Sale', 'Purchase'];
  ownedDisplayValue = '';
  ltvDisplayValue = '';

  ngOnChanges(changes: SimpleChanges) {
    if(this.isLtv){
      this.pastFilterForm.get('ltvMin').setValue(0)
      this.pastFilterForm.get('ltvMax').setValue(30)
      this.pastFilterForm.get('ownedMin').reset()
      this.pastFilterForm.get('ownedMax').reset()
      this.updateLtvDisplay();
      this.filterData()
    } else {
      this.pastFilterForm.get('ltvMin').reset();
      this.pastFilterForm.get('ltvMax').reset()
    }
    if(this.isLongTerm){
      this.pastFilterForm.get('ownedMin').setValue(7)
      this.pastFilterForm.get('ownedMax').setValue('')
      this.pastFilterForm.get('ltvMin').reset();
      this.pastFilterForm.get('ltvMax').reset()
      this. updateOwnedDisplay();
      this.filterData()
    } else {
      this.pastFilterForm.get('ownedMin').reset()
      this.pastFilterForm.get('ownedMax').reset()
    }
  }

  ngOnInit(): void {
    this.tableColumnList = this.displayedColumns;
    if (this.appState?.companyInfoValue?.appConfig?.pastClientConfig?.enabled == true) this.getPastClients();
    this.clientDashboardState.pastClientList$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (!res) return;
      if (res) this.pastClientInfo = res;
      let tableData = [];
      if (this.limitRows && res.data) {
        tableData = res.data.slice(0, this.limitRows);
      } else {
        tableData = res.data;
      }
      this.tableDataSource.data = tableData ?? [];

      this.tableDataSource.paginator = this.paginator;
      this.pastUsersCount = res.totalPastClient;
    });

  this.clientDashboardState.openAddContactNoteSheet$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((status) => {
      this.openAddContactNoteSheetValue = status;
    });
    this.clientDashboardState.openCreateMailSheet$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((status) => {
      this.openCreateMailSheetValue = status;
    });

    ['ltvMin', 'ltvMax', 'ownerByMin', 'ownerByMax'].forEach((key) => {
      this.pastFilterForm.get(key)?.valueChanges.subscribe(() => {
        this.pastFilterForm.updateValueAndValidity({ onlySelf: true });
      });
    });

    const nav = history.state;
    if (nav?.actionKey && nav?.rowData) {
      this.actions(nav.actionKey, nav.rowData);
    }

    this.clientDashboardState.pastSearchClientList$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (!res || !res.data) return;
      let tableData = [];
      if (this.limitRows) {
        tableData = res.data.slice(0, this.limitRows);
      } else {
        tableData = res.data;
      }
      this.tableDataSource.data = tableData ?? [];
      this.pastUsersCount = res.totalPastClient;
      setTimeout(() => {
        this.tableDataSource.sort = this.sort;
        this.tableDataSource.paginator = this.paginator;
      });
    });

    this.pastFilterForm.get('ownedMin')!.valueChanges.subscribe(() => this.updateOwnedDisplay());

    this.pastFilterForm.get('ownedMax')!.valueChanges.subscribe(() => this.updateOwnedDisplay());

    this.pastFilterForm.get('ltvMin')?.valueChanges.subscribe(() => {
      this.updateLtvDisplay();
    });

    this.pastFilterForm.get('ltvMax')?.valueChanges.subscribe(() => {
      this.updateLtvDisplay();
    });
  }

  updateOwnedDisplay() {
    const min = this.pastFilterForm.get('ownedMin')?.value;
    const max = this.pastFilterForm.get('ownedMax')?.value;

    if (min != null && min !== '' && max != null && max !== '') {
      this.ownedDisplayValue = `${min} - ${max} Years`;
    } else if (min != null && min !== '') {
      this.ownedDisplayValue = `From ${min} Years`;
    } else if (max != null && max !== '') {
      this.ownedDisplayValue = `Up to ${max} Years`;
    } else {
      this.ownedDisplayValue = '';
    }
  }

  updateLtvDisplay() {
    const min = this.pastFilterForm.get('ltvMin')?.value;
    const max = this.pastFilterForm.get('ltvMax')?.value;

    if (min != null && min !== '' && max != null && max !== '') {
      this.ltvDisplayValue = `${min}% - ${max}%`;
    } else if (min != null && min !== '') {
      this.ltvDisplayValue = `From ${min}%`;
    } else if (max != null && max !== '') {
      this.ltvDisplayValue = `Up to ${max}%`;
    } else {
      this.ltvDisplayValue = '';
    }
  }

  getAddressComponentsFromStandardAddress(standardAddress: string): Json {
    let addressComponentList = standardAddress?.split(',');
    if (!addressComponentList || addressComponentList?.length < 3) return null;
    let stateAndZip = addressComponentList[2];
    let stateAndZipList = stateAndZip?.split(' ');
    let addressComponent: any = {
      StreetAddress: addressComponentList[0],
      City: addressComponentList[1],
      State: stateAndZipList[1],
      PostalCode: stateAndZipList[2],
    };
    return addressComponent;
  }

  viewProperty(addr) {
    addr.StreetAddress = addr?.StreetAddress?.replace(/\s*#.*$/, '').trim(); // Remove '#' and everything after it
    this.propertyState.propertyDetailsSheetInput = addr;
  }


  getPastClients(params?: any) {
    this.clientDashboardService.getPastClient(params || {}).subscribe((res: any) => { });
  }

  ngAfterViewInit() {
    this.tableDataSource.paginator = this.paginator;
    this.tableDataSource.sort = this.sort;
    // Promise.resolve().then(() => {
    //   this.tableDataSource.sort = this.sort;
    // });
    this.tableDataSource.sortingDataAccessor = (item: any, property: string) => {
      switch (property) {
        case 'transactionDate':
          return item.closeDate ? new Date(item.closeDate).getTime() : Number.MAX_SAFE_INTEGER;

        case 'currentValue':
          return item.currentValue != null ? Number(item.currentValue) : -1;

        case 'equity':
          return item.equity != null ? Number(item.equity) : -1;

        case 'ltv':
          return item.ltv != null ? Number(item.ltv) : -1;
        case 'transactionType':
          return item.transactionType ? item.transactionType.toLowerCase() : '';
        case 'occupancy':
          if (item.occupancy === 'Owner') return 0;
          if (item.occupancy === 'Tenant') return 1;
          return 2;
        case 'currentStatus':
          return item.currentStatus ? item.currentStatus.toLowerCase() : '';
        case 'ownedFor':
          return item.ownedFor ? item.ownedFor.toLowerCase() : '';

        default:
          return '';
      }
    };
  }

  sortTable(sortState: Sort) {
    this.paginator.pageIndex = 0;
    this.pastClientsFetchParams.limit = 10;
    this.pastClientsFetchParams.offset = 0;
    this.pastClientsFetchParams.orderBy = sortState.active;
    this.pastClientsFetchParams.orderType = sortState.direction;
  }

  pageChange(event) {
    if (this.pastClientsFetchParams.offset != event.pageSize * event.pageIndex) {
      this.pastClientsFetchParams.limit = event.pageSize;
      this.pastClientsFetchParams.offset = event.pageSize * event.pageIndex;
    } else {
      this.pastClientsFetchParams.limit = event.pageSize;
      this.paginator.pageIndex = 0;
      this.pastClientsFetchParams.offset = 0;
    }
  }

  actions(key: any, row?: any) {
    if (key == 'addContact') {
      this.clientDashboardState.openAddContactNoteSheetValue = true;
      this.clientDashboardState.addContactSheetContextValue = {
        source: 'past-clients',
        data: row,
      };
    }

    if (key == 'sendMail') {
      this.clientDashboardState.openCreateMailSheetValue = true;
      this.clientDashboardState.mailSheetContextValue = {
        source: 'past-clients',
        data: row,
      };
    }
  }

  minMaxValidator(minKey: string, maxKey: string, errorKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const fg = group as FormGroup;

      const minCtrl = fg.get(minKey);
      const maxCtrl = fg.get(maxKey);

      if (!minCtrl || !maxCtrl) return null;

      if (minCtrl.value == null || minCtrl.value === '' || maxCtrl.value == null || maxCtrl.value === '') {
        this.removeError(maxCtrl, errorKey);
        return null;
      }

      const min = +minCtrl.value;
      const max = +maxCtrl.value;

      if (max <= min) {
        maxCtrl.setErrors({ ...(maxCtrl.errors || {}), [errorKey]: true });
        return { [errorKey]: true };
      }

      this.removeError(maxCtrl, errorKey);
      return null;
    };
  }

  private removeError(control: AbstractControl, key: string) {
    if (control.hasError(key)) {
      const errors = { ...control.errors };
      delete errors[key];
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
  }

  getFullNames(...persons: { first?: string; last?: string }[]): string {
    const names = persons.map((p) => `${p.first || ''} ${p.last || ''}`.trim()).filter((name) => name.length > 0);

    return names.length ? names.join(', ') : '—';
  }

  filterData() {
    if (this.pastFilterForm.invalid) {
      // this.pastFilterForm.markAllAsTouched();
      return;
    }
    const formValue = this.pastFilterForm.value;
    const isAllEmpty = Object.values(formValue).every((value) => value === null || value === '');
    if (!isAllEmpty) {
      this.getPastClients(formValue);
    } else {
      this.getPastClients();
    }
    // this.reset.emit('resetFilter');
    this.paginator.pageIndex = 0;
  }

  toggleRow(row: any, event: any) {
    if (event.checked) {
      this.selection.add(row);
    } else {
      this.selection.delete(row);
    }
    this.onSelectionChange();
  }

  onSelectionChange() {
    this.selectedRows = Array.from(this.selection);
    // do whatever you want with selected JSON
  }

  formatAmount(value: number | string): string {
  const amount = Number(String(value).replace(/[$,]/g, ''));
  if (isNaN(amount)) {
    return '$0';
  }
  if (Math.abs(amount) >= 1000000) {
    return `$${(amount / 1000000).toFixed(2)}M`;
  }
  if (Math.abs(amount) >= 1000) {
    return `$${(amount / 1000).toFixed(2)}K`;
  }
  return `$${amount.toLocaleString('en-US')}`;
}

  addToCampaign() {
    // console.log(this.selectedRows);
  }

  resetFilter() {
    this.reset.emit('resetFilter');
    this.pastFilterForm.reset();
    this.getPastClients();
    this.paginator.pageSize = 10;
    this.paginator.pageIndex = 0;
  }
}
